import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import { api } from "./api";
import "bootstrap/dist/css/bootstrap.min.css";
import "leaflet/dist/leaflet.css";

export default function TechMap({ onLogout }) {
  // -------------------------------
  // REFS para elementos do mapa
  // -------------------------------
  const mapRef = useRef(null);           // Referência para o objeto do mapa
  const mapContainer = useRef(null);     // Referência para o div do mapa
  const markerRef = useRef(null);        // Marker do técnico
  const pulseCircleRef = useRef(null);   // Círculo de pulso em torno do técnico
  const ctoLayerRef = useRef(null);      // LayerGroup para CTOs

  // -------------------------------
  // STATES
  // -------------------------------
  const [ctos, setCtos] = useState([]);                // Lista de CTOs filtradas
  const [techPosition, setTechPosition] = useState(null); // Posição atual do técnico
  const [selectedCTO, setSelectedCTO] = useState(null);   // CTO selecionada
  const [nearestCTO, setNearestCTO] = useState(null);     // CTO mais próxima

  let pulseValue = 0; // valor do pulso para animação

  // -------------------------------
  // FUNÇÕES AUXILIARES DE DISTÂNCIA
  // -------------------------------
  function getDistanceKm(lat1, lon1, lat2, lon2) {
    // Calcula a distância em KM usando a fórmula de Haversine
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function getDistanceMeters(lat1, lon1, lat2, lon2) {
    return getDistanceKm(lat1, lon1, lat2, lon2) * 1000;
  }

  // -------------------------------
  // FUNÇÃO PARA CRIAR ÍCONE QUADRADO PARA CTO
  // -------------------------------
  function ctoSquareIcon(signal) {
    const color =
      signal == null || signal === 0
        ? "#FF0000"
        : signal >= -26
        ? "#00CC00"
        : signal > -32
        ? "#FFA500"
        : "#FF0000";

    return L.divIcon({
      html: `<div style="
        width: 20px;
        height: 20px;
        background-color: ${color};
        border: 3px solid #000;
        box-sizing: border-box;
      "></div>`,
      className: "",
      iconSize: [20, 20],
      iconAnchor: [10, 10], // centraliza o quadrado no ponto
    });
  }

  // -------------------------------
  // INICIALIZAÇÃO DO MAPA
  // -------------------------------
  useEffect(() => {
    if (mapRef.current) return; // evita recriação do mapa
    const div = mapContainer.current;
    if (!div) return;

    // Cria o mapa (sem centro inicial fixo, vamos centralizar na posição do técnico)
    mapRef.current = L.map(div, { zoom: 15, zoomControl: true });

    // -------------------------------
    // CAMADAS BASE
    // -------------------------------
    const tileLayers = {
      osm: L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }),
      satellite: L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 19, attribution: "Esri" }
      ),
      outdoors: L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", {
        maxZoom: 17,
        attribution: "OpenTopoMap",
      }),
    };

    // Adiciona camada padrão
    tileLayers.osm.addTo(mapRef.current);

    // Controle de camadas no mapa
    const baseMaps = {
      "OSM (Padrão)": tileLayers.osm,
      "Satélite (ESRI)": tileLayers.satellite,
      "Outdoors (Topo)": tileLayers.outdoors,
    };
    L.control.layers(baseMaps, {}).addTo(mapRef.current);

    // -------------------------------
    // RASTREAMENTO DA POSIÇÃO DO TÉCNICO
    // -------------------------------
    trackLocation();

    // -------------------------------
    // ANIMAÇÃO DE PULSO
    // -------------------------------
    startPulseAnimation();
  }, []);

  // -------------------------------
  // ANIMAÇÃO DE PULSO DO TÉCNICO
  // -------------------------------
  function startPulseAnimation() {
    function animate() {
      pulseValue += 0.02;
      if (pulseValue > 1) pulseValue = 0;

      if (pulseCircleRef.current) {
        const radius = 6 + pulseValue * 40;
        const opacity = 0.6 - pulseValue * 0.6;
        pulseCircleRef.current.setRadius(radius);
        pulseCircleRef.current.setStyle({ opacity, fillOpacity: opacity });
      }

      requestAnimationFrame(animate);
    }
    animate();
  }

  // -------------------------------
  // RENDERIZAÇÃO DAS CTOs NO MAPA
  // -------------------------------
  const renderCTOLayers = useCallback((ctosData) => {
    if (!mapRef.current) return;
    if (ctoLayerRef.current) mapRef.current.removeLayer(ctoLayerRef.current);

    const group = L.layerGroup();
    ctosData.forEach((cto) => {
      const geo = JSON.parse(cto.location);

      const marker = L.marker([geo.coordinates[1], geo.coordinates[0]], {
        icon: ctoSquareIcon(cto.sinal),
      });

      marker.on("click", () => {
        setSelectedCTO({
          name: cto.nome,
          sinal: cto.sinal,
          lotacao: cto.lotacao,
          splitter: cto.splitter,
          location: cto.location,
        });
      });

      marker.addTo(group);
    });

    group.addTo(mapRef.current);
    ctoLayerRef.current = group;
  }, []);

  // -------------------------------
  // CARREGAR CTOs PRÓXIMAS AO TÉCNICO
  // -------------------------------
  const loadCTOs = useCallback(async () => {
    if (!techPosition) return;
    const res = await api.get("/cto/list");
    let filtered = res.data;

    const [techLat, techLon] = techPosition;

    filtered = filtered.filter((cto) => {
      const { coordinates } = JSON.parse(cto.location);
      const dist = getDistanceKm(techLat, techLon, coordinates[1], coordinates[0]);
      return dist <= 0.6; // Filtra CTOs até 600m do técnico
    });

    setCtos(filtered);
    renderCTOLayers(filtered);
  }, [techPosition, renderCTOLayers]);

  // -------------------------------
  // RASTREAMENTO DA POSIÇÃO DO TÉCNICO
  // -------------------------------
  function trackLocation() {
    if (!navigator.geolocation) return alert("Seu navegador não permite geolocalização.");

    const updatePosition = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const coords = [latitude, longitude];
          setTechPosition(coords);
          mapRef.current.setView([latitude, longitude], 15);

          // Cria ou atualiza marker do técnico
          if (!markerRef.current) {
            markerRef.current = L.marker([latitude, longitude], {
              icon: L.divIcon({
                html: `<div style="width:16px;height:16px;background:#4285f4;border-radius:50%;border:3px solid white;box-shadow:0 0 10px rgba(66,133,244,0.8);"></div>`,
                className: "",
                iconSize: [16, 16]
              }),
            }).addTo(mapRef.current);

            pulseCircleRef.current = L.circle([latitude, longitude], {
              radius: 10,
              color: "#4287f5",
              fillColor: "#4287f5"
            }).addTo(mapRef.current);
          } else {
            markerRef.current.setLatLng([latitude, longitude]);
            pulseCircleRef.current.setLatLng([latitude, longitude]);
          }
        },
        (err) => alert("Não foi possível obter sua localização."),
        { enableHighAccuracy: true }
      );
    };

    updatePosition();             // primeira atualização
    setInterval(updatePosition, 40000); // atualiza a cada 40s
  }

  // -------------------------------
  // CARREGA CTO MAIS PRÓXIMA
  // -------------------------------
  useEffect(() => {
    if (!techPosition) return;
    loadCTOs();

    api.post("/cto/nearest", { lat: techPosition[0], lon: techPosition[1] })
      .then((res) => setNearestCTO(res.data))
      .catch(() => {});
  }, [techPosition, loadCTOs]);

  // -------------------------------
  // CALCULA DISTÂNCIA DA CTO SELECIONADA
  // -------------------------------
  let selectedDistance = null;
  if (selectedCTO && techPosition) {
    try {
      const loc = JSON.parse(selectedCTO.location || "{}");
      if (loc.coordinates) {
        selectedDistance = getDistanceMeters(
          techPosition[0],
          techPosition[1],
          loc.coordinates[1],
          loc.coordinates[0]
        );
      }
    } catch (e) { console.log(e); }
  }

  // -------------------------------
  // RENDERIZAÇÃO FINAL
  // -------------------------------
  return (
    <div style={{ padding: "1px", position: "relative" }}>
      {/* BOTÃO SAIR */}
      <div style={{ textAlign: "center", marginBottom: "10px" }}>
        <button className="btn btn-danger" onClick={onLogout}>Sair</button>
      </div>

      <h3 style={{ textAlign: "center" }}>Painel Técnico – CTOs</h3>

      {/* CTO MAIS PRÓXIMA */}
      {nearestCTO && (
        <div style={{ textAlign: "center", marginBottom: 2 }}>
          <b>CTO mais próxima:</b> {nearestCTO.nome}<br/>
          Distância: {nearestCTO.distance.toFixed(0)} metros
        </div>
      )}

      {/* MAPA */}
      <div
        id="map"
        ref={mapContainer}
        style={{ width: "100%", height: "85vh", borderRadius: "10px" }}
      />

      {/* MODAL CTO SELECIONADA */}
      {selectedCTO && (
        <div style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", padding:20, borderRadius:8, minWidth:300, maxWidth:400, textAlign:"center", position:"relative" }}>
            <button onClick={()=>setSelectedCTO(null)} style={{ position:"absolute", top:8, right:12, background:"none", border:"none", fontSize:24, cursor:"pointer" }}>×</button>
            <h3>CTO Selecionada</h3>
            <p><b>Nome:</b> {selectedCTO.name}</p>
            <p><b>Sinal:</b> {selectedCTO.sinal != null && !isNaN(Number(selectedCTO.sinal)) ? Number(selectedCTO.sinal).toFixed(2) : <span style={{color:"red"}}><strong>LOS</strong></span>}</p>
            <p><b>Lotação:</b> {selectedCTO.lotacao ?? "–"}</p>
            <p><b>Splitter:</b> {selectedCTO.splitter ?? "–"}</p>
            <p><b>Distância até o técnico:</b> {selectedDistance != null ? `${selectedDistance.toFixed(0)} metros` : "–"}</p>
          </div>
        </div>
      )}
    </div>
  );
}
